import { FC, Fragment, useReducer } from 'react';

import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { Download } from 'lucide-react';
import { isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DatePicker } from '@mui/x-date-pickers';
import { PickerValue } from '@mui/x-date-pickers/internals';

import { ExportRewardsForm, initialValues } from './form';

import { rewardsService } from 'v2/api/rewardsService';
import { RewardHistoryFilter } from 'v2/types/RewardModel';
import { handleHttpError } from 'v2/utils';

interface ExportRewardsHistoryButtonProps {
  filter: RewardHistoryFilter;
}

export const ExportRewardsHistoryButton: FC<ExportRewardsHistoryButtonProps> = ({ filter }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const formik = useFormik<ExportRewardsForm>({
    initialValues,
    onSubmit: (values) => {
      if (isNullish(values.startDate) || isNullish(values.endDate)) {
        toast.error('Please specify the start/end date');
      } else {
        trigger({ startDate: values.startDate.format(), endDate: values.endDate.format() });
      }
    },
  });

  const { trigger, isMutating } = useSWRMutation(
    ['exportRewardsHistory', filter],
    rewardsService.exportRewardsHistory,
    {
      onSuccess: () => {
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const handleChangeDate = (field: keyof ExportRewardsForm) => (value: PickerValue) => {
    formik.setFieldValue(field, value);
  };

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Download width={20} height={20} />} variant="contained" size="large">
        Export
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Export Rewards History</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="form-group">
            <DatePicker
              value={formik.values.startDate}
              onChange={handleChangeDate('startDate')}
              label="Start Date"
              format="DD.MM.YYYY"
            />

            <DatePicker
              value={formik.values.endDate}
              onChange={handleChangeDate('endDate')}
              maxDate={dayjs()}
              label="End Date"
              format="DD.MM.YYYY"
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button loading={isMutating} type="submit" variant="contained">
              Export
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
